import actions from '~/shared/store/actions';
import {selectUserRequiredActionsMap, selectUserData, selectCurrentModalName} from '~/shared/store/selectors';
import {openUpdateUserDetailsPageOrModal} from '~/shared/services/navigation';
import {isMoneycardActivationRequired, safeJsonParse} from '~/shared/utils/general';
import {getCurrentLocation, pushRoute} from '~/shared/router';
import {routeNames} from '~/shared/routes';
import {A11Y_LOCALSTORAGE_KEY_NAME, LocalStorageKeys} from '~/shared/consts/localStorageConsts';
import {safeLocalStorage} from '~/shared/utils/safeStorage';
import {checkTimePastWithHours} from '~/shared/utils/checkTimePast';
import {AppStore} from '~/shared/store/configStore';

const pagesToIgnoreUpdateUserDetails = [
  routeNames.updateUserDetails,
  routeNames.info,
  routeNames.unsubscribe,
  routeNames.gfoOptOut,
  routeNames.join,
  routeNames.gov,
  routeNames.guestCards,
];

export const invokeUserRequiredActionIfNeeded = ({store}: {store: AppStore}) => {
  const state = store.getState();
  const userActionsRequiredMap = selectUserRequiredActionsMap(state);
  const user = selectUserData(state);
  const updatePasswordRequired = user?.updatePasswordRequired;
  const storedLastRedirectDate = safeLocalStorage.getItem(LocalStorageKeys.LAST_OTL_REDIRECT_TIMESTAMP);
  const parsedLastRedirectDate = storedLastRedirectDate && safeJsonParse(storedLastRedirectDate);
  const is12hoursPast = checkTimePastWithHours({storedDate: parsedLastRedirectDate?.timeStemp || null, hours: 12});
  // redirect to otl is not required if the user is already required to update user details.
  const isRequireOtlRedirect = user?.userEnabledFeatures.includes('RedirectToOTL') && !user?.userRequiredActions?.updateUserDetailsRequired;
  
  const {routeName} = getCurrentLocation();

  const shouldPreventUpdateUserDetailsAndMoneycardActivation =
    pagesToIgnoreUpdateUserDetails.includes(routeName) || selectCurrentModalName(state).includes('update_user_details');

  if (isRequireOtlRedirect && is12hoursPast) {
    safeLocalStorage.setJSON(LocalStorageKeys.LAST_OTL_REDIRECT_TIMESTAMP, {
      timeStemp: Date.now(),
    });
    pushRoute('/join', {hard: true});
    return;
  }

  if (shouldPreventUpdateUserDetailsAndMoneycardActivation) {
    return;
  }

  const moneycardActivationRequired = isMoneycardActivationRequired({
    moneycardActivationRequired: !!userActionsRequiredMap.moneycardActivationRequired,
  });

  if (moneycardActivationRequired) {
    store.dispatch(actions.setCurrentModal('activateMoneycard'));
    return;
  }

  if (userActionsRequiredMap.updateUserDetailsRequired || updatePasswordRequired) {
    openUpdateUserDetailsPageOrModal();
  }

};

export const setInitialA11yValues = (store: AppStore) => {
  const a11yInitialValues = safeLocalStorage.getItem(A11Y_LOCALSTORAGE_KEY_NAME);

  if (a11yInitialValues) {
    const parsedA11yInitalValues = JSON.parse(a11yInitialValues);
    const {constrastEnabled, outlineEnabled} = parsedA11yInitalValues;

    if (constrastEnabled) {
      store.dispatch(actions.toggleContrastMode());
    }

    if (outlineEnabled) {
      store.dispatch(actions.toggleOutlieMode());
    }
  }
};
